/**
 * Settings - NFR-10.6.1 and FR-8.6 (AC5).
 *
 * The six keys ARCHITECTURE.md seeds, each editable in place. There is no create
 * and no delete, which is the deviation recorded in DECISIONS.md: the keys are
 * read BY NAME from four services and one view, so a seventh row would be read by
 * nothing and deleting a seeded one would silently revert that feature to a
 * literal in the source rather than to anything an admin chose. The spec names
 * `PATCH` as the write, and PATCH is what this screen does.
 *
 * The control is chosen from `value_type`, and the value is sent back as the
 * string it arrived as - the server validates it against the type and returns the
 * canonical form. That matters more here than anywhere else in the panel: a value
 * that does not parse as its type does not fail on this screen, it fails inside
 * `v_member_access` on every member request, because `setting_int` casts at READ
 * time. So the refusal is at the door and the copy explains what was expected.
 *
 * The success message is the server's, and it says members see the change
 * immediately - which is true and is the whole point of FR-8.6: nothing is cached
 * and nothing is baked into the app binary.
 */

import { useEffect, useState } from "react";

import {
  Button,
  Card,
  CardHeader,
  ErrorNotice,
  Field,
  Input,
  Page,
  Select,
  SuccessNotice,
} from "../components/primitives";
import { fetchSettings, updateSetting, type Setting, type SettingList } from "../lib/api/settings";
import { useMutation, useQuery } from "../lib/hooks";
import { formatDateTime } from "../lib/format";

export function SettingsScreen() {
  const query = useQuery<SettingList>(() => fetchSettings(), []);
  const { data, error, loading, reload } = query;

  if (error && !data) {
    return (
      <Page>
        <ErrorNotice error={error} onRetry={reload} />
      </Page>
    );
  }

  if (loading && !data) {
    return (
      <Page>
        <Card>
          <div className="stack" style={{ gap: 12 }}>
            <div className="skeleton" style={{ height: 18, width: "30%" }} />
            <div className="skeleton" style={{ height: 40 }} />
            <div className="skeleton" style={{ height: 40 }} />
          </div>
        </Card>
      </Page>
    );
  }

  if (!data) return null;

  return (
    <Page>
      <Card>
        <CardHeader
          title="Member-facing settings"
          hint="Most of these are read on every member request, so a change here takes effect on their next action - no deploy and no app update."
          actions={
            <Button size="sm" onClick={reload} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </Button>
          }
        />
        <div className="stack" style={{ gap: 0 }}>
          {data.items.map((setting) => (
            <SettingRow key={setting.key} setting={setting} onSaved={reload} />
          ))}
        </div>
      </Card>

      <p className="notice">
        Every change is recorded in the audit log against your account, with the value
        before and after.
      </p>
    </Page>
  );
}

/**
 * One setting, one row, one Save.
 *
 * The draft is local state so a half-typed number is never sent, and Save is
 * disabled until the value actually differs - resubmitting the same value is a
 * no-op on the server (no audit row), so offering the button would invite a click
 * that does nothing visible.
 */
function SettingRow({ setting, onSaved }: { setting: Setting; onSaved: () => void }) {
  const [draft, setDraft] = useState(setting.value);
  const [saved, setSaved] = useState<string | null>(null);

  // A reload after another admin's change must not leave a stale draft in the
  // input, so the field follows the server whenever the server value changes and
  // the field is not being edited.
  useEffect(() => {
    setDraft(setting.value);
  }, [setting.value]);

  const mutation = useMutation(
    (value: string) => updateSetting(setting.key, value),
    {
      onSuccess: (result) => {
        setSaved(result.message);
        onSaved();
      },
    },
  );

  const unchanged = draft === setting.value;

  return (
    <div
      style={{
        padding: "16px 0",
        borderBottom: "1px solid var(--border-warm)",
      }}
    >
      <div className="row row--between" style={{ alignItems: "flex-start", gap: 24 }}>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <div className="field__label" style={{ color: "var(--black)", letterSpacing: 0 }}>
            {setting.key}
          </div>
          <p className="text-small text-meta" style={{ margin: "4px 0 0" }}>
            {setting.description ?? "No description recorded for this key."}
          </p>
          <p className="text-small text-meta" style={{ margin: "4px 0 0" }}>
            {setting.value_type}
            {setting.updated_by
              ? ` · last changed ${formatDateTime(setting.updated_at)}`
              : " · never changed by an admin"}
          </p>
        </div>

        <div className="row row--tight" style={{ flex: "0 0 auto", alignItems: "flex-end" }}>
          <div style={{ width: 160 }}>
            <Field label="Value">
              {(id) => <SettingControl id={id} setting={setting} value={draft} onChange={setDraft} />}
            </Field>
          </div>
          <Button
            variant="accent"
            disabled={mutation.pending || unchanged}
            onClick={() => {
              setSaved(null);
              void mutation.run(draft.trim());
            }}
          >
            {mutation.pending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {mutation.error ? (
        <div style={{ marginTop: 10 }}>
          <ErrorNotice error={mutation.error} />
        </div>
      ) : null}

      {saved ? (
        <div style={{ marginTop: 10 }}>
          <SuccessNotice message={saved} onDismiss={() => setSaved(null)} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The control for a `value_type`.
 *
 * `boolean` is a select rather than a checkbox on purpose: a checkbox has no
 * "unchanged" state to compare against, and this screen's Save button depends on
 * knowing whether the value differs from what the server holds.
 */
function SettingControl({
  id,
  setting,
  value,
  onChange,
}: {
  id: string;
  setting: Setting;
  value: string;
  onChange: (value: string) => void;
}) {
  if (setting.value_type === "boolean") {
    return (
      <Select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="true">true</option>
        <option value="false">false</option>
      </Select>
    );
  }

  if (setting.value_type === "json") {
    return (
      <Input
        id={id}
        className="input--mono"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  return (
    <Input
      id={id}
      // `type="number"` for the two numeric types, which also gets the browser's
      // own up/down keys; the value stays a STRING in state because that is what
      // the column holds and what the API validates.
      type={setting.value_type === "integer" || setting.value_type === "decimal" ? "number" : "text"}
      step={setting.value_type === "decimal" ? "any" : "1"}
      min={setting.value_type === "integer" ? 0 : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
